/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import CurrentPrice from './current-price'
import ListPrice from './list-price'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    symbol,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    isLineItem = false,
    showMarkdownText = false,
    salePriceColor
}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange} = priceData
    const renderCurrentPrice = (isRange) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={currentPrice * quantity}
            as="b"
            currency={currency}
            symbol={symbol}
            isRange={isRange}
            isOnSale={isOnSale}
            salePriceColor={salePriceColor}
            {...currentPriceProps}
        />
    )

    const renderListPrice = (isRange) =>
        listPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                symbol={symbol}
                price={listPrice * quantity}
                isRange={isRange}
                isOnSale={isOnSale}
                {...listPriceProps}
            />
        )

    const renderPriceSet = (isRange) => (
        <Box
            gap="10px"
            display="flex"
            className="render-price-set"
            flexDirection={{base: isLineItem ? 'row-reverse' : 'unset', xl: 'unset'}}
            width="fit-content"
        >
            {isOnSale && (
                <Box display="inline-flex" gap="5px">
                    {showMarkdownText && (
                        <Text
                            className="total-markdown-price"
                            {...cartStyles.lineItems.item.totalMarkdownPrice}
                        >
                            <FormattedMessage
                                id="total.markdown.price"
                                defaultMessage="Total Markdown Price"
                            />
                        </Text>
                    )}
                    {isOnSale && renderListPrice(isRange)}
                </Box>
            )}{' '}
            {renderCurrentPrice(isRange)}
        </Box>
    )

    if (isASet) {
        return renderPriceSet(true)
    }

    if (isMaster) {
        return renderPriceSet(isRange)
    }

    return <Box className="display-price">{renderPriceSet(false)}</Box>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    symbol: PropTypes.string,
    listPriceProps: PropTypes.object,
    quantity: PropTypes.number,
    isLineItem: PropTypes.bool,
    showMarkdownText: PropTypes.bool,
    salePriceColor: PropTypes.string
}

export default DisplayPrice
